import { useMutation } from "@tanstack/react-query";
import { API_ROUTES } from "../api/ApiRoutes";
import { getPrivateInstance } from "./_privateInstance";

export interface StaffPreferencesPatch {
  language?: string;
  timezone?: string;
}

export interface StaffPreferencesResult {
  /**
   * `true` when the backend accepted the PATCH; `false` when the endpoint
   * returned 404 (not deployed yet — contract C1 feature detection). In the
   * `false` case callers keep localStorage-only persistence and surface no
   * user-facing error.
   */
  persisted: boolean;
  data: unknown;
}

/**
 * PATCH /admin-panel-staff/v2/staff/me/preferences (self-scoped, contract C1).
 * Plain async function (not a hook) so non-component callers — e.g. the login
 * guest-language transition in useAdminAuth — can share the exact same
 * feature-detection behavior.
 */
export async function patchStaffMePreferences(
  patch: StaffPreferencesPatch,
): Promise<StaffPreferencesResult> {
  try {
    const response = await getPrivateInstance().patch(
      API_ROUTES.PATCH_STAFF_ME_PREFERENCES,
      patch,
    );
    return { persisted: true, data: response?.data ?? null };
  } catch (error) {
    const status = (error as { response?: { status?: number } })?.response
      ?.status;
    if (status === 404) {
      return { persisted: false, data: null };
    }
    throw error;
  }
}

/** React Query mutation wrapper for the header language selector. */
export const useUpdateStaffPreferences = () =>
  useMutation({
    mutationKey: ["PATCH_STAFF_ME_PREFERENCES"],
    mutationFn: patchStaffMePreferences,
  });
